import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CloneScenarioForm, ProjectScenarioDto } from './api';
import useApi from './useApi';
import { MINE_MODEL_KEY, MINE_MODEL_SUMMARY_KEY, RETICULATION_DATA_EXPORT_KEY } from './useMineModels';
import { RECIPES_KEY } from './useRecipes';
import { STOPE_BACKFILLS_KEY } from './usePours';

export const SCENARIOS_KEY = 'scenarios';

export interface ScenariosQueryParams {
  mineModelId: string;
}

export const useScenariosQuery = ({ mineModelId }: ScenariosQueryParams, options?: { enabled?: boolean }) => {
  const api = useApi();
  return useQuery<ProjectScenarioDto[]>(
    [SCENARIOS_KEY, { mineModelId }],
    () => api.scenarios.list(mineModelId),
    {
      enabled: !!mineModelId && (options?.enabled ?? true),
    },
  );
};

export interface CloneScenarioMutationVariables {
  mineModelId: string;
  form: CloneScenarioForm;
}

export const useCloneScenarioMutation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<string, unknown, CloneScenarioMutationVariables>(
    (variables) => api.scenarios.clone(variables.mineModelId, variables.form),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([SCENARIOS_KEY, { mineModelId: variables.mineModelId }]);
      },
    },
  );
};

export interface DeleteScenarioMutationVariables {
  mineModelId: string;
  scenarioId: string;
}

export const useDeleteScenarioMutation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, DeleteScenarioMutationVariables>(
    (variables) => api.scenarios.delete(variables.mineModelId, variables.scenarioId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([SCENARIOS_KEY, { mineModelId: variables.mineModelId }]);
        queryClient.invalidateQueries(MINE_MODEL_KEY, { exact: false });
        queryClient.invalidateQueries(MINE_MODEL_SUMMARY_KEY, { exact: false });
        queryClient.invalidateQueries(RETICULATION_DATA_EXPORT_KEY, { exact: false });
        queryClient.invalidateQueries(RECIPES_KEY, { exact: false });
        queryClient.invalidateQueries(STOPE_BACKFILLS_KEY, { exact: false });
      },
    },
  );
};
